/**
 * The destination fields of the Rename / Move dialog: Rename = one name field (same parent); Move = the destination
 * file system (the account's containers from the tree's snapshot, or just the source's on a scoped attachment) and a
 * path. Fields are frozen once a move has started (a partial or uncertain move keeps its destination).
 */
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import type { MoveDraft } from './moveDialogModel';

type Props = {
  draft: MoveDraft;
  fileSystems: string[];
  frozen: boolean;
  onChange: (next: Partial<MoveDraft>) => void;
};

export function MovePathFields({ draft, fileSystems, frozen, onChange }: Props) {
  if (draft.action === 'rename') {
    return (
      <div className="space-y-2">
        <Label htmlFor="storage-hns-move-name">New name</Label>
        <Input id="storage-hns-move-name" spellCheck={false} autoComplete="off" value={draft.name} disabled={frozen} title={frozen ? 'The move has started; its destination is fixed.' : undefined} autoFocus onChange={(event) => onChange({ name: event.target.value })} data-testid="storage-hns-move-name" />
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      <div className="space-y-2">
        {/* U31 / A19: "container", as the tree and the Azure portal say (a Data Lake file system is a container). */}
        <Label htmlFor="storage-hns-move-file-system">Destination container</Label>
        <Select value={draft.fileSystem} disabled={frozen} onValueChange={(fileSystem) => onChange({ fileSystem })}>
          <SelectTrigger id="storage-hns-move-file-system" data-testid="storage-hns-move-file-system" title={frozen ? 'The move has started; its destination is fixed.' : undefined}><SelectValue /></SelectTrigger>
          <SelectContent>{fileSystems.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="storage-hns-move-path">Destination path</Label>
        <Input id="storage-hns-move-path" spellCheck={false} autoComplete="off" value={draft.path} disabled={frozen} title={frozen ? 'The move has started; its destination is fixed.' : undefined} autoFocus onChange={(event) => onChange({ path: event.target.value })} data-testid="storage-hns-move-path" />
        <p className="text-xs text-muted-foreground">The full path inside the container; the folder above it must exist (Azure does not create it for a move).</p>
      </div>
    </div>
  );
}
